"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { templateFieldState } from "@/lib/categories/inheritance";
import type { DescriptionTemplateInfo } from "@/services/category.service";
import { Fold } from "./fold";

const LENGTH_PRESETS = {
  short: 75,
  medium: 150,
  long: 250,
} as const;
const MAX_TARGET_WORD_COUNT = 1000;

function renderDirectives(directives: string[]): string {
  if (directives.length === 0) return "";
  if (directives.length === 1) return directives[0] ?? "";
  const labelled = directives.map(
    (directive, index) =>
      `Directive ${index + 1} (${index === 0 ? "broadest" : "more specific"}):\n${directive}`,
  );
  return `${labelled.join("\n\n")}\n\nApply all directives. If they conflict, the later, more specific child directive always takes precedence over the earlier parent directive.`;
}

interface DescriptionTemplateFieldProps {
  info: DescriptionTemplateInfo | null;
  value: string;
  hasOwn: boolean;
  mode: "replace" | "extend";
  targetWordCount: number | null;
  hasOwnTarget: boolean;
  targetTouched: boolean;
  readOnly: boolean;
  isCatalogueWide: boolean;
  inheritedFrom: string | null;
  onChange: (value: string) => void;
  onModeChange: (mode: "replace" | "extend") => void;
  onTargetWordCountChange: (target: number | null) => void;
  onCustomise: () => void;
  onReset: () => void;
}

export function DescriptionTemplateField({
  info,
  value,
  hasOwn,
  mode,
  targetWordCount,
  hasOwnTarget,
  targetTouched,
  readOnly,
  isCatalogueWide,
  inheritedFrom,
  onChange,
  onModeChange,
  onTargetWordCountChange,
  onCustomise,
  onReset,
}: DescriptionTemplateFieldProps) {
  const fieldState = templateFieldState({ isCatalogueWide, hasOwn, inheritedFrom });
  const state = {
    "catalogue-wide": hasOwn ? "Catalogue default" : "No catalogue default",
    own: mode === "extend" ? "Extends parent" : "Replaces parent",
    inherited: `Same as ${inheritedFrom}`,
    none: "No style set",
  }[fieldState];

  const effectiveDirectives = hasOwn
    ? mode === "extend"
      ? [...(info?.parentDirectives ?? []), value.trim()].filter(Boolean)
      : [value.trim()].filter(Boolean)
    : (info?.resolvedDirectives ?? []);
  const effectiveDirective = renderDirectives(effectiveDirectives);
  const shownTarget = hasOwnTarget
    ? targetWordCount
    : targetTouched
      ? (info?.parentTargetWordCount ?? null)
      : (info?.targetWordCount ?? null);
  const preset = !hasOwnTarget
    ? "inherit"
    : Object.entries(LENGTH_PRESETS).find(([, count]) => count === shownTarget)?.[0]
      ?? "custom";

  const choosePreset = (choice: string) => {
    if (choice === "inherit") {
      onTargetWordCountChange(null);
      return;
    }
    if (choice === "custom") {
      onTargetWordCountChange(
        shownTarget && !Object.values(LENGTH_PRESETS).some((count) => count === shownTarget)
          ? shownTarget
          : 151,
      );
      return;
    }
    onTargetWordCountChange(LENGTH_PRESETS[choice as keyof typeof LENGTH_PRESETS]);
  };

  return (
    <Fold title="How descriptions are written" state={state}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {fieldState === "catalogue-wide"
              ? "This style is used whenever a category does not replace it."
              : fieldState === "own"
                ? mode === "extend"
                  ? "The broader rules stay active; this category's rule wins if they conflict."
                  : "This category's rule replaces all broader description rules."
                : fieldState === "inherited"
                  ? <>Same as <strong className="text-foreground">{inheritedFrom}</strong>.</>
                  : "Nothing here or above sets a style, so Youzu writes freely."}
          </p>
          {!hasOwn && !readOnly && (
            <Button variant="outline" size="sm" className="h-7" onClick={onCustomise}>
              {isCatalogueWide ? "Set catalogue style" : "Write descriptions differently here"}
            </Button>
          )}
        </div>

        {hasOwn && !isCatalogueWide && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Relationship to broader rules</label>
            <Select
              value={mode}
              disabled={readOnly}
              onValueChange={(next) => onModeChange(next as "replace" | "extend")}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="replace">Replace broader rules</SelectItem>
                <SelectItem value="extend">Extend broader rules</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <Textarea
          value={hasOwn ? value : (info?.resolved ?? "")}
          readOnly={!hasOwn}
          disabled={readOnly}
          rows={4}
          placeholder="For example: start with the material, then explain where the product is useful."
          className={hasOwn ? "text-sm" : "border-dashed bg-muted/40 text-sm text-muted-foreground"}
          onChange={(event) => onChange(event.target.value)}
        />

        {hasOwn && !readOnly && (
          <button
            type="button"
            className="text-xs font-medium text-pink-700 underline underline-offset-2 hover:text-foreground"
            onClick={onReset}
          >
            {isCatalogueWide ? "Clear the catalogue style" : "Inherit the broader style"}
          </button>
        )}

        <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-[minmax(0,1fr)_140px]">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Description length</label>
            <Select value={preset} disabled={readOnly} onValueChange={choosePreset}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">
                  {isCatalogueWide ? "System default" : "Inherit / system default"}
                </SelectItem>
                <SelectItem value="short">Short · 75 words</SelectItem>
                <SelectItem value="medium">Medium · 150 words</SelectItem>
                <SelectItem value="long">Long · 250 words</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="description-word-target" className="text-xs font-medium">
              Target words
            </label>
            <Input
              id="description-word-target"
              type="number"
              min={1}
              max={MAX_TARGET_WORD_COUNT}
              step={1}
              disabled={readOnly || shownTarget === null}
              value={shownTarget ?? ""}
              onChange={(event) => {
                const next = Number(event.target.value);
                if (
                  Number.isInteger(next)
                  && next > 0
                  && next <= MAX_TARGET_WORD_COUNT
                ) {
                  onTargetWordCountChange(next);
                }
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground sm:col-span-2">
            This is a soft target, not a truncation rule. {!hasOwnTarget && shownTarget !== null
              ? "It is currently inherited; editing the number creates an override here."
              : "Youzu may vary slightly to keep the copy complete and natural."}
          </p>
        </div>

        <div className="rounded-md border bg-muted/30 px-3 py-2.5">
          <p className="mb-1 text-[10.5px] uppercase tracking-wide text-muted-foreground">
            Effective directive sent to Youzu
          </p>
          <p className="whitespace-pre-wrap text-sm">
            {effectiveDirective || <span className="text-muted-foreground">No directive set.</span>}
          </p>
        </div>
      </div>
    </Fold>
  );
}
